import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '~/Services/api';
import Header from '~/Components/Header';
import OrderBy from '~/Components/OrderBy';
import Loading from '~/Components/Loading';
import ScrollToTopButton from '~/Components/ScrollToTopButton';
import GenericModal from '~/Components/GenericModal';
import ResumoChaves from '~/Components/Negocios/CaixaAprovacao/ResumoChaves';
import CardChave from '~/Components/Negocios/CaixaAprovacao/CardChave';
import ModalAprovarReprovar from '~/Components/Negocios/CaixaAprovacao/ModalAprovarReprovar';
import ChaveId from '~/Components/Negocios/CaixaAprovacao/ChaveId';
import { orderingOptions, blankView, modalViews } from './options';
import FiltrosChaves from './FiltrosChaves';
import {
  FaFilter,
  FaTrashAlt,
  FaSearchPlus,
  FaSearchMinus,
} from 'react-icons/fa';

export default function CaixaAprovacao() {
  const [chaves, setChaves] = useState([]);
  const [chavesSelecionadas, setChavesSelecionadas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showInfo, setShowInfo] = useState(false);
  const [modalView, setModalView] = useState(blankView);

  const [ordering, setOrdering] = useState(orderingOptions[0]);

  const [filtros, setFiltros] = useState({
    conta: '',
    usuarioDigitador: '',
    fornecedor: '',
    documento: '',
    modalidade: '',
    ges: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    conta: '',
    usuarioDigitador: '',
    fornecedor: '',
    documento: '',
    modalidade: '',
    ges: '',
  });

  const [showModalFiltros, setShowModalFiltros] = useState(false);
  const [showModalAprovarReprovar, setShowModalAprovarReprovar] =
    useState(false);
  const [shouldAprovar, setShouldAprovar] = useState(false);

  async function buscarChavesAprovacao() {
    setIsLoading(true);
    try {
      const response = await api.buscarChavesAprovacao();
      setChaves(response.sort(ordering.func));
      setChavesSelecionadas([]);
    } catch (error) {
      if (error.status !== 401) toast.error('Falha ao buscar as chaves');
    } finally {
      setIsLoading(false);
    }
  }
  const memoizedBuscarChavesAprovacao = useCallback(buscarChavesAprovacao, []);

  useEffect(() => {
    memoizedBuscarChavesAprovacao();
  }, [memoizedBuscarChavesAprovacao]);

  const toggleChaveSelected = useCallback(
    chave => {
      if (
        chave.nivel === 0 &&
        chave.codigo_usuario_digitacao !== chave.codigo_usuario_caixa
      )
        return;

      const novoEstado = !chave.selected;

      const novasChaves = chaves.map(c => {
        if (chave.codigo_grupo && c.codigo_grupo === chave.codigo_grupo) {
          return { ...c, selected: novoEstado };
        }
        if (!chave.codigo_grupo && c.codigo === chave.codigo) {
          return { ...c, selected: novoEstado };
        }
        return c;
      });

      setChaves(novasChaves);

      if (chave.codigo_grupo) {
        const chavesDoGrupo = novasChaves.filter(
          c => c.codigo_grupo === chave.codigo_grupo
        );

        if (novoEstado) {
          const novosItens = chavesDoGrupo.filter(
            c => !chavesSelecionadas.some(cs => cs.codigo === c.codigo)
          );
          setChavesSelecionadas(prev => [...prev, ...novosItens]);
        } else {
          setChavesSelecionadas(prev =>
            prev.filter(c => c.codigo_grupo !== chave.codigo_grupo)
          );
        }
      } else {
        if (novoEstado) {
          const existente = novasChaves.find(c => c.codigo === chave.codigo);
          setChavesSelecionadas(prev => [...prev, existente]);
        } else {
          setChavesSelecionadas(prev =>
            prev.filter(c => c.codigo !== chave.codigo)
          );
        }
      }
    },
    [chaves, chavesSelecionadas]
  );

  const filtrarChaves = chave => {
    const { conta, usuarioDigitador, fornecedor, documento, modalidade, ges } =
      appliedFilters;

    return (
      (conta === '' || chave.codigo_conta_portador === conta) &&
      (usuarioDigitador === '' ||
        chave.usuario_digitacao.nome === usuarioDigitador) &&
      (fornecedor === '' || chave.codigo_conta_fornecedor === fornecedor) &&
      (documento === '' || chave.tipo_documento === documento) &&
      (modalidade === '' || chave.descricao_modalidade === modalidade) &&
      (ges === '' || chave.codigo_grupo_empresa === ges)
    );
  };

  const chavesFiltradas = chaves.filter(filtrarChaves);

  const contasMap = new Map();
  chaves.forEach(chave => {
    if (!contasMap.has(chave.codigo_conta_portador)) {
      contasMap.set(chave.codigo_conta_portador, {
        value: chave.codigo_conta_portador,
        label: `${chave.codigo_conta_portador}: ${chave.conta_portador.apelido_conta}`,
      });
    }
  });
  const contas = Array.from(contasMap.values());

  const fornecedoresMap = new Map();
  chaves.forEach(chave => {
    if (!fornecedoresMap.has(chave.codigo_conta_fornecedor)) {
      fornecedoresMap.set(chave.codigo_conta_fornecedor, {
        value: chave.codigo_conta_fornecedor,
        label: `${chave.codigo_conta_fornecedor}: ${chave.fornecedor.apelido_conta}`,
      });
    }
  });
  const fornecedores = Array.from(fornecedoresMap.values());

  const documentos = [
    ...new Set(chaves.map(chave => chave.tipo_documento)),
  ].map(doc => ({
    value: doc,
    label: doc,
  }));

  const usuarios = [
    ...new Set(chaves.map(chave => chave.usuario_digitacao.nome)),
  ].map(usuario => ({
    value: usuario,
    label: usuario,
  }));

  const ges = [...new Set(chaves.map(chave => chave.codigo_grupo_empresa))].map(
    ge => ({
      value: ge,
      label: ge,
    })
  );

  function beginAprovarReprovar(shouldAprovar = true) {
    setShowModalAprovarReprovar(true);
    setShouldAprovar(shouldAprovar);
  }

  function onAprovadoReprovado() {
    toast.success(
      `Chaves ${shouldAprovar ? 'aprovadas' : 'reprovadas'} com sucesso`
    );
    setChavesSelecionadas([]);
    memoizedBuscarChavesAprovacao();
  }

  function toggleInfoModal(chave, infoType, parameter = null) {
    const isShowing = !showInfo;
    setShowInfo(isShowing);
    if (isShowing) {
      const view = modalViews[infoType];
      view.chave = chave;
      view.content = parameter || chave;
      setModalView(view);
    } else setModalView(blankView);
  }

  function reorder(e) {
    const ordering = orderingOptions.filter(o => o.value === e.target.value)[0];
    setOrdering(ordering);
    setChaves(chaves.sort(ordering.func));
  }

  function selecionarTodas() {
    const gruposProcessados = new Set();
    const novasChavesSelecionadas = [];

    const novasChaves = chaves.map(chave => {
      const estaFiltrada = filtrarChaves(chave);

      if (!estaFiltrada) {
        return chave;
      }

      if (
        chave.nivel === 0 &&
        chave.codigo_usuario_digitacao !== chave.codigo_usuario_caixa
      ) {
        return chave;
      }

      if (chave.codigo_grupo) {
        if (!gruposProcessados.has(chave.codigo_grupo)) {
          gruposProcessados.add(chave.codigo_grupo);
          const chavesDoGrupo = chaves.filter(
            c => c.codigo_grupo === chave.codigo_grupo
          );
          novasChavesSelecionadas.push(...chavesDoGrupo);
        }
        return { ...chave, selected: true };
      } else {
        novasChavesSelecionadas.push(chave);
        return { ...chave, selected: true };
      }
    });

    setChaves(novasChaves);
    setChavesSelecionadas(novasChavesSelecionadas);
  }

  function aplicarFiltros() {
    setAppliedFilters(filtros);
    setShowModalFiltros(false);
  }

  function limparFiltros() {
    setFiltros({
      conta: '',
      usuarioDigitador: '',
      fornecedor: '',
      documento: '',
      modalidade: '',
      ges: '',
    });
    setAppliedFilters({
      conta: '',
      usuarioDigitador: '',
      fornecedor: '',
      documento: '',
      modalidade: '',
      ges: '',
    });
    setShowModalFiltros(false);
  }

  function limparChavesSelecionadas() {
    const novasChaves = chaves.map(chave => {
      chave.selected = false;
      return chave;
    });
    setChaves(novasChaves);
    setChavesSelecionadas([]);
  }

  function mainContent() {
    return (
      <div className="container flex-fill">
        <div className="row">
          <div className="col-12 col-md-4 order-md-2">
            <ResumoChaves
              chavesSelecionadas={chavesSelecionadas}
              onClickSelecionarTodos={selecionarTodas}
              onAprovar={() => beginAprovarReprovar(true)}
              onReprovar={() => beginAprovarReprovar(false)}
              onLimparSelecao={limparChavesSelecionadas}
            />
          </div>
          <div className="col-12 col-md-8 mt-3">
            <div className="d-sm-flex justify-content-between align-items-center">
              <span className="mr-auto w-50 d-block d-sm-inline">
                <strong>{chavesFiltradas.length}</strong> chaves na caixa
              </span>
              <div className="d-flex d-sm-none flex-column w-100 mt-2">
                <div className="mb-2">
                  <OrderBy
                    options={orderingOptions}
                    value={ordering.value}
                    onChange={reorder}
                    className="w-100"
                  />
                </div>
                <div className="d-flex justify-content-between">
                  <Button
                    className="btn-sm d-flex align-items-center justify-content-center flex-fill mr-1 h-100"
                    variant="primary"
                    onClick={() => setShowModalFiltros(true)}
                  >
                    <FaFilter className="mr-1" />
                    <span>Filtros</span>
                  </Button>
                  <Button
                    className="btn-sm d-flex align-items-center justify-content-center flex-fill h-100"
                    variant="secondary"
                    onClick={limparFiltros}
                  >
                    <FaTrashAlt className="mr-1" />
                    <span>Limpar</span>
                  </Button>
                </div>
              </div>
              <div className="d-none d-sm-flex justify-content-between justify-content-sm-start align-items-center w-100 w-sm-auto">
                <OrderBy
                  options={orderingOptions}
                  value={ordering.value}
                  onChange={reorder}
                  className="mr-1"
                />
                <div className="d-flex justify-content-between justify-content-sm-start">
                  <Button
                    className="mr-sm-1 btn-sm d-flex align-items-center justify-content-center flex-fill mr-1 h-100"
                    variant="primary"
                    onClick={() => setShowModalFiltros(true)}
                  >
                    <FaFilter className="mr-sm-1" />
                    <span className="d-none d-sm-inline">Filtros</span>
                  </Button>
                  <Button
                    className="btn-sm d-flex align-items-center justify-content-center flex-fill h-100"
                    variant="secondary"
                    onClick={limparFiltros}
                  >
                    <FaTrashAlt className="mr-sm-1" />
                    <span className="d-none d-sm-inline">Limpar</span>
                  </Button>
                </div>
              </div>
            </div>
            <div className="row mt-1">
              <div className="col-12">
                {appliedFilters.conta && (
                  <span className="badge badge-primary mr-2">
                    Conta: {appliedFilters.conta}
                  </span>
                )}
                {appliedFilters.usuarioDigitador && (
                  <span className="badge badge-primary mr-2">
                    Usuário Digitador: {appliedFilters.usuarioDigitador}
                  </span>
                )}
                {appliedFilters.fornecedor && (
                  <span className="badge badge-primary mr-2">
                    Fornecedor: {appliedFilters.fornecedor}
                  </span>
                )}
                {appliedFilters.documento && (
                  <span className="badge badge-primary mr-2">
                    Documento: {appliedFilters.documento}
                  </span>
                )}
              </div>
            </div>
            {chavesFiltradas.map(chave => (
              <CardChave
                key={chave.codigo}
                chave={chave}
                onCardClick={() => toggleChaveSelected(chave)}
                onShowInfoRequested={toggleInfoModal}
              />
            ))}
          </div>
        </div>
        <ScrollToTopButton
          scrollStepInPx="50"
          delayInMs="16.66"
          className="d-block d-sm-none"
        />
      </div>
    );
  }

  return (
    <>
      <Header title="Caixa de Aprovação">
        <Button onClick={memoizedBuscarChavesAprovacao} disabled={isLoading}>
          ATUALIZAR
        </Button>
      </Header>
      {isLoading && <Loading />}
      {!isLoading && mainContent()}
      <ModalAprovarReprovar
        show={showModalAprovarReprovar}
        onCancel={() => setShowModalAprovarReprovar(false)}
        aprovando={shouldAprovar}
        onSuccess={onAprovadoReprovado}
        chaves={chavesSelecionadas}
      />
      <GenericModal
        show={showInfo}
        title={modalView.title}
        onCancel={toggleInfoModal}
        content={modalView.render(modalView.content)}
        size={modalView.title === 'Digitalização' ? 'lg' : 'md'}
        dialogClassName={
          modalView.title === 'Digitalização' ? 'modal-digitalizacao' : ''
        }
      />
      <Modal show={showModalFiltros} onHide={() => setShowModalFiltros(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filtros</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FiltrosChaves
            filtros={filtros}
            setFiltros={setFiltros}
            contas={contas}
            usuarios={usuarios}
            fornecedores={fornecedores}
            documentos={documentos}
            chaves={chaves}
            ges={ges}
            onApplyFilters={aplicarFiltros}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={aplicarFiltros}>
            Aplicar Filtros
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
